import type { Properties } from 'posthog-js';
import { captureEvent, captureException } from './posthog';

/**
 * Reporta erro fatal/non-fatal pra observabilidade do admin. Espelho do
 * `apps/app/src/lib/errorReport.ts` — o admin web não tem Crashlytics SDK
 * nem Sentry hoje, então a única visibilidade sobre erros em produção é
 * PostHog Cloud US (`captureEvent` envia o evento com a severidade
 * embutida). Veja CLAUDE.md (seção "Observabilidade — sem DevTools dos
 * clientes") para a regra completa.
 *
 * `console.error` em paralelo é só pra DevTools local em dev — nunca
 * substitui o report formal.
 *
 * Origem: #12 — commit 1e260186a, 2026-06-16 18:58 +0200.
 *
 * Usado por: telas e libs do `apps/admin` (`rg reportFatal apps/admin/src`).
 *
 * Cada report sai em DOIS eventos (#315): o nomeado (`event`), que painéis e
 * auditoria noturna leem, e um `$exception` com `error_event = event`, o
 * único que a tela Error Tracking do PostHog enxerga. Não tirar nenhum.
 *
 * Histórico:
 *  - 2026-10-09 16:41 +0200 — (#315) passa a emitir também `$exception`.
 */
export function reportFatal(event: string, meta: Properties = {}): void {
  const payload: Properties = {
    severity: 'FATAL',
    ...meta,
  };
  captureEvent(event, payload);
  captureException(exceptionFromReport(event, payload), { ...payload, error_event: event });
  console.error(`[FATAL] ${event}`, payload);
}

/**
 * Igual ao `reportFatal`, com `severity: 'NON_FATAL'` e `console.warn` — para
 * falha que a tela contorna (parse que cai em ERROR, fallback, listener que
 * desiste).
 *
 * Origem: #12 — commit 1e260186a, 2026-06-16.
 *
 * Usado por: hooks e libs do `apps/admin` (`rg reportNonFatal apps/admin/src`).
 */
export function reportNonFatal(event: string, meta: Properties = {}): void {
  const payload: Properties = {
    severity: 'NON_FATAL',
    ...meta,
  };
  captureEvent(event, payload);
  captureException(exceptionFromReport(event, payload), { ...payload, error_event: event });
  console.warn(`[NON_FATAL] ${event}`, payload);
}

/**
 * Remonta um `Error` a partir do payload do report, para o `$exception`.
 * Mesma função do `apps/app/src/lib/errorReport.ts` (#315): os call sites
 * passam o erro achatado por `serializeError`; sem esses campos, o nome do
 * evento vira tipo e mensagem, para a issue no PostHog levar esse nome.
 */
function exceptionFromReport(event: string, payload: Properties): Error {
  const { errorName, errorMessage, errorStack } = payload as Record<string, unknown>;
  const error = new Error(typeof errorMessage === 'string' && errorMessage !== '' ? errorMessage : event);
  error.name = typeof errorName === 'string' && errorName !== '' ? errorName : event;
  if (typeof errorStack === 'string' && errorStack !== '') error.stack = errorStack;
  return error;
}

/**
 * Serializa erros pra envio no payload de telemetria (PostHog não aceita
 * `Error` direto). Stack truncado em 1.5kB pra não estourar o evento.
 *
 * Origem: #12 — commit 1e260186a, 2026-06-16.
 *
 * Contrato: nunca lança (objeto cíclico cai no `Object#toString`). Devolve
 * `errorName`/`errorMessage`/`errorStack` — os mesmos campos que
 * `exceptionFromReport` lê para montar o `$exception`.
 *
 * Usado por: quem chama `reportFatal`/`reportNonFatal` com um erro capturado.
 */
export function serializeError(err: unknown): Properties {
  if (err instanceof Error) {
    return {
      errorName: err.name,
      errorMessage: err.message,
      errorStack: err.stack?.slice(0, 1500) ?? null,
    };
  }
  if (typeof err === 'object' && err !== null) {
    try {
      return { errorMessage: JSON.stringify(err).slice(0, 1500) };
    } catch {
      // Cycle ou getter throwing — usa Object#toString consciente.
      return { errorMessage: Object.prototype.toString.call(err) };
    }
  }
  if (typeof err === 'string') return { errorMessage: err.slice(0, 1500) };
  if (typeof err === 'number' || typeof err === 'boolean')
    return { errorMessage: String(err) };
  return { errorMessage: 'unknown_error_shape' };
}
